import type {
  AuthToken,
  Network,
  MutationUpdateNetworkAvailableLocalesArgs,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthTokenKey } from '../../utils/keys/authToken.keys.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'

export const useUpdateNetworkAvailableLocals = (options?: {
  useMutationOptions?: UseMutationOptions<
    Network,
    Error,
    MutationUpdateNetworkAvailableLocalesArgs
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<Network, Error, MutationUpdateNetworkAvailableLocalesArgs>(
    variables =>
      client.mutation({
        name: 'updateNetworkAvailableLocales',
        args: {
          fields: 'basic',
          variables,
        },
      }),
    {
      onMutate: variables => {
        const authTokenKey = getAuthTokenKey()
        queryClient.setQueriesData<AuthToken>(authTokenKey, old => ({
          ...old,
          network: {
            ...old.network,
            availableLocales: variables.availableLocales,
          },
        }))
        queryClient.setQueriesData<Network>(getNetworkKey(), old => ({
          ...old,
          availableLocales: variables.availableLocales,
        }))
      },
      onSettled: () => {
        const authTokenKey = getAuthTokenKey()
        const networkKey = getNetworkKey()
        queryClient.invalidateQueries(authTokenKey)
        queryClient.invalidateQueries(networkKey)
      },
      ...useMutationOptions,
    },
  )
}
